import React, { useRef, useState, useEffect } from "react";
import { UploadCloud, CheckCircle2, XCircle, Loader2, Send, Plus, X } from "lucide-react";
import { FadeUp } from "@/components/motion";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_BYTES = 10 * 1024 * 1024;
const EXPECTED = 6;

const EXPECTED_SIZES = [
  { n: 1, w: 656, h: 544 },
  { n: 2, w: 1184, h: 336 },
  { n: 3, w: 1440, h: 336 },
  { n: 4, w: 1920, h: 336 },
  { n: 5, w: 2432, h: 336 },
  { n: 6, w: 656, h: 250 },  // WSA-only position
];
const sizeLabel = (s) => (s.n === 6 ? `Size 6 (WSA only, ${s.w} × ${s.h})` : `Size ${s.n} (${s.w} × ${s.h})`);

const SITE_OPTIONS = [
  { v: "wsa", label: "WhichSchoolAdvisor.com" },
  { v: "sc", label: "SchoolsCompared.com" },
  { v: "both", label: "Both" },
];

/**
 * AdPreflight — the advertising page's campaign upload channel. Schools drop
 * creatives one at a time; each is checked instantly (format, 10 MB raw,
 * 50-frame cap, exact Elegant upload dimensions) and ONLY passing files join
 * the campaign. After each one they're asked "add another?" until they stop
 * (or reach the 5 expected sizes), then we collect school/company, the advert
 * URL(s) — one per chosen site — and contact details. The server re-validates
 * every file on submit and the ad ops team is emailed.
 */
const AdPreflight = () => {
  const inputRef = useRef(null);
  const pendingRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [files, setFiles] = useState([]);            // [{file, result}]
  const [fail, setFail] = useState(null);            // last failed check result
  const [stage, setStage] = useState("drop");        // drop | added | notice | details | sent
  const [submitError, setSubmitError] = useState(null);
  const [previews, setPreviews] = useState([]);      // object URLs for the success panel
  const [form, setForm] = useState({ name: "", email: "", company: "", sites: "", url_wsa: "", url_sc: "", website: "" });

  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

  // Coverage + duplicate analysis against the five Elegant sizes
  const covered = new Set(files.map((f) => f.result.matched_size).filter(Boolean));
  const missing = EXPECTED_SIZES.filter((s) => !covered.has(s.n));
  const bySize = {};
  files.forEach((f) => {
    const k = f.result.matched_size;
    if (k) (bySize[k] = bySize[k] || []).push(f);
  });
  const dupes = Object.entries(bySize).filter(([, v]) => v.length > 1);
  // Size 6 is a WSA-only position — SchoolsCompared-only campaigns expect five
  const expectedForSites = form.sites === "sc" ? EXPECTED_SIZES.filter((s) => s.n !== 6) : EXPECTED_SIZES;
  const siteMissing = expectedForSites.filter((s) => !covered.has(s.n));

  const checkFile = async (f) => {
    if (!f) return;
    pendingRef.current = f;
    setFail(null);
    setSubmitError(null);
    if (f.size > MAX_BYTES) {
      setFail({ filename: f.name, checks: [{ key: "weight", label: "Weight", pass: false,
                detail: `${(f.size / 1048576).toFixed(2)} MB raw — over the 10 MB limit` }] });
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await fetch(`${API}/api/ad-preflight`, { method: "POST", body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Check failed (HTTP ${res.status})`);
      }
      const result = await res.json();
      if (!result.pass) {
        setFail(result);
      } else {
        const next = [...files, { file: f, result }];
        setFiles(next);
        setStage(next.length >= EXPECTED ? "details" : "added");
      }
    } catch (e) {
      setFail({ filename: f.name, checks: [{ key: "upload", label: "Upload", pass: false, detail: String(e.message || e) }] });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeFile = (i) => {
    const next = files.filter((_, j) => j !== i);
    setFiles(next);
    if (!next.length) setStage("drop");
  };

  const submitCampaign = async (e) => {
    e.preventDefault();
    if (!files.length || sending) return;
    setSending(true);
    setSubmitError(null);
    try {
      const fd = new FormData();
      files.forEach(({ file }) => fd.append("files", file));
      ["name", "email", "company", "sites", "url_wsa", "url_sc", "website"].forEach((k) => fd.append(k, form[k]));
      const res = await fetch(`${API}/api/ad-submissions`, { method: "POST", body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const msg = typeof err.detail === "string" ? err.detail : (err.detail?.message || `Upload failed (HTTP ${res.status})`);
        throw new Error(msg);
      }
      setPreviews(files.map(({ file }) => URL.createObjectURL(file)));
      setStage("sent");
    } catch (e2) {
      setSubmitError(String(e2.message || e2));
    } finally {
      setSending(false);
    }
  };

  const fieldCls = "w-full rounded-lg bg-white/5 border border-white/15 px-4 py-3 font-sans text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-primary transition-colors";

  const CheckList = ({ checks }) => (
    <ul className="divide-y divide-white/10 bg-white/[0.03]">
      {checks.map((c) => (
        <li key={c.key} className="px-5 py-3.5 flex items-start gap-3" data-testid={`preflight-check-${c.key}`}>
          {c.pass ? (
            <CheckCircle2 size={16} className="text-emerald-400 mt-0.5 shrink-0" />
          ) : (
            <XCircle size={16} className="text-red-400 mt-0.5 shrink-0" />
          )}
          <div>
            <p className="font-sans text-sm font-bold text-white">{c.label}</p>
            <p className="font-sans text-sm text-white/65">{c.detail}</p>
          </div>
        </li>
      ))}
    </ul>
  );

  // Advisory shown whenever two or more creatives match the same Elegant size
  const DupeWarning = () => (
    <div className="mt-4 rounded-xl border border-amber-400/40 bg-amber-400/10 px-5 py-4 space-y-2" data-testid="duplicate-warning">
      {dupes.map(([size, group]) => {
        const meta = EXPECTED_SIZES.find((s) => s.n === Number(size));
        return (
          <p key={size} className="font-sans text-sm text-amber-200 leading-relaxed">
            You have uploaded {group.length} adverts of the same size ({meta.w} × {meta.h}).
            We can only publish one of these. If you would like these to be combined to run as one
            advert, please create a GIF file with a tool like Photoshop. Avoid Canva which creates
            a movie and multiple frames.
          </p>
        );
      })}
    </div>
  );

  return (
    <FadeUp>
      <div className="mt-10 bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8" data-testid="ad-preflight">
        <p className="kicker mb-3" style={{ color: "hsl(var(--primary))" }}>Send us your ad</p>
        <p className="headline text-2xl md:text-3xl uppercase text-white">Upload your campaign — checked instantly</p>
        <p className="mt-3 text-base text-white/70 max-w-3xl">
          A campaign is usually {EXPECTED} responsive sizes of the same ad (five for SchoolsCompared.com-only
          campaigns — Size 6 runs on WhichSchoolAdvisor.com). Drop each file below —
          we check it against every rule above in seconds, then add the next. Only files that pass
          every check can be sent to our ad ops team.
        </p>

        {/* Added creatives */}
        {files.length > 0 && stage !== "sent" && (
          <div className="mt-6" data-testid="campaign-file-list">
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2">
              Your campaign — {files.length} of {EXPECTED} sizes
            </p>
            <div className="flex flex-wrap gap-2">
              {files.map(({ result }, i) => (
                <span key={i} data-testid={`campaign-file-${i}`} className="inline-flex items-center gap-2 font-mono text-xs text-white bg-white/10 border border-emerald-400/30 rounded-full pl-3 pr-2 py-1.5">
                  <CheckCircle2 size={13} className="text-emerald-400" />
                  {result.filename}{result.matched_size ? ` · Size ${result.matched_size}` : ""}
                  <button type="button" onClick={() => removeFile(i)} data-testid={`campaign-file-remove-${i}`} className="text-white/50 hover:text-red-400 transition-colors" title="Remove">
                    <X size={13} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Dropzone */}
        {stage !== "details" && stage !== "sent" && (
          <>
            <button
              type="button"
              data-testid="preflight-dropzone"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); checkFile(e.dataTransfer.files?.[0]); }}
              className={`mt-6 w-full rounded-2xl border-2 border-dashed px-6 py-10 flex flex-col items-center justify-center gap-3 transition-colors ${
                dragOver ? "border-primary bg-primary/10" : "border-white/25 hover:border-primary/70 hover:bg-white/5"
              }`}
            >
              {busy ? <Loader2 size={28} className="text-primary animate-spin" /> : <UploadCloud size={28} className="text-primary" />}
              <span className="font-sans text-sm font-bold text-white">
                {busy ? "Checking…" : files.length ? `Add size ${files.length + 1} of ${EXPECTED} — choose a file or drag it here` : "Choose a file or drag it here"}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                GIF · PNG · JPG — up to 10 MB raw each
              </span>
            </button>
            <input
              ref={inputRef}
              type="file"
              accept=".gif,.png,.jpg,.jpeg,image/gif,image/png,image/jpeg"
              className="hidden"
              data-testid="preflight-input"
              onChange={(e) => checkFile(e.target.files?.[0])}
            />
          </>
        )}

        {/* Failed check */}
        {fail && (
          <div className="mt-6 rounded-xl overflow-hidden border border-white/10" data-testid="preflight-results">
            <div className="px-5 py-4 flex items-center gap-3 bg-red-500/15">
              <XCircle size={20} className="text-red-400 shrink-0" />
              <p className="font-sans text-sm font-bold text-white" data-testid="preflight-verdict">
                {fail.filename} — please fix the items marked below and upload again.
              </p>
            </div>
            <CheckList checks={fail.checks} />
          </div>
        )}

        {/* Add another? */}
        {stage === "added" && (
          <div className="mt-6 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-5 py-5" data-testid="add-another-prompt">
            <p className="font-sans text-sm font-bold text-white flex items-center gap-2">
              <CheckCircle2 size={18} className="text-emerald-400" />
              {files[files.length - 1].result.filename} passes every check — added to your campaign.
            </p>
            {missing.length > 0 ? (
              <p className="font-sans text-sm text-white/65 mt-1" data-testid="still-needed">
                Still to add: {missing.map(sizeLabel).join(" · ")}
              </p>
            ) : (
              <p className="font-sans text-sm text-emerald-300 mt-1" data-testid="still-needed">
                All five sizes covered.
              </p>
            )}
            <p className="font-sans text-sm text-white/65 mt-1">Would you like to add another size?</p>
            {dupes.length > 0 && <DupeWarning />}
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setStage("drop")}
                data-testid="add-another-btn"
                className="inline-flex items-center gap-2 font-sans text-sm font-bold border-2 border-primary text-primary rounded-full px-5 py-2.5 hover:bg-primary hover:text-white transition-colors"
              >
                <Plus size={15} /> Yes — add another ad
              </button>
              <button
                type="button"
                onClick={() => setStage(files.length < EXPECTED ? "notice" : "details")}
                data-testid="continue-btn"
                className="inline-flex items-center gap-2 font-sans text-sm font-bold bg-primary text-primary-foreground rounded-full px-5 py-2.5 hover:bg-white hover:text-ink transition-colors"
              >
                No — continue
              </button>
            </div>
          </div>
        )}

        {/* Count notice */}
        {stage === "notice" && (
          <div className="mt-6 rounded-xl border border-white/15 bg-white/5 px-5 py-5" data-testid="count-notice">
            <p className="font-sans text-sm text-white">
              You've uploaded <strong>{files.length}</strong> {files.length === 1 ? "advert" : "adverts"} of the 5 (SC) or 6 (WSA) responsive sizes we would normally expect for a campaign.
            </p>
            {missing.length > 0 && (
              <p className="font-sans text-sm text-amber-200 mt-2" data-testid="missing-sizes">
                Missing: {missing.map(sizeLabel).join(" · ")}.
              </p>
            )}
            {dupes.length > 0 && <DupeWarning />}
            <p className="font-sans text-sm text-white/65 mt-2">That's fine — you can send what you have and add the rest later.</p>
            <button
              type="button"
              onClick={() => setStage("details")}
              data-testid="count-ok-btn"
              className="mt-4 inline-flex items-center gap-2 font-sans text-sm font-bold bg-primary text-primary-foreground rounded-full px-6 py-2.5 hover:bg-white hover:text-ink transition-colors"
            >
              OK — continue
            </button>
          </div>
        )}

        {/* Campaign details */}
        {stage === "details" && (
          <form onSubmit={submitCampaign} className="mt-6" data-testid="preflight-form">
            {dupes.length > 0 && <DupeWarning />}
            {siteMissing.length > 0 && (
              <p className="font-sans text-sm text-white/65 mb-4" data-testid="details-missing">
                Sending {files.length} of {expectedForSites.length} sizes — missing: {siteMissing.map(sizeLabel).join(" · ")}.
              </p>
            )}
            {form.sites === "sc" && (
              <p className="font-sans text-xs text-white/50 mb-4" data-testid="details-sc-note">
                Size 6 (300 × 125) is a WhichSchoolAdvisor.com-only position — not needed for a SchoolsCompared-only campaign.
              </p>
            )}
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3">
              Almost done — tell us about the campaign
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <input type="text" required placeholder="Your name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} className={fieldCls} data-testid="preflight-name" />
              <input type="email" required placeholder="Email address" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} className={fieldCls} data-testid="preflight-email" />
              <input type="text" required placeholder="Name of School / Company" value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })} className={fieldCls} data-testid="preflight-company" />
            </div>

            <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mt-6 mb-3">
              Campaign to be run on
            </p>
            <div className="flex flex-wrap gap-3" data-testid="campaign-sites">
              {SITE_OPTIONS.map((o) => (
                <button
                  key={o.v} type="button"
                  onClick={() => setForm({ ...form, sites: o.v })}
                  data-testid={`campaign-site-${o.v}`}
                  className={`font-sans text-sm font-bold rounded-full px-5 py-2.5 border-2 transition-colors ${
                    form.sites === o.v
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-white/25 text-white/70 hover:border-primary/70 hover:text-white"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>

            {(form.sites === "wsa" || form.sites === "both") && (
              <input type="text" required placeholder="Advert URL for WhichSchoolAdvisor.com (where the ad clicks through to)"
                value={form.url_wsa} onChange={(e) => setForm({ ...form, url_wsa: e.target.value })}
                className={`${fieldCls} mt-4`} data-testid="preflight-url-wsa" />
            )}
            {(form.sites === "sc" || form.sites === "both") && (
              <input type="text" required placeholder="Advert URL for SchoolsCompared.com (where the ad clicks through to)"
                value={form.url_sc} onChange={(e) => setForm({ ...form, url_sc: e.target.value })}
                className={`${fieldCls} mt-4`} data-testid="preflight-url-sc" />
            )}

            {/* honeypot — invisible to humans */}
            <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
              tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" data-testid="preflight-honeypot" />

            <div className="mt-6 flex flex-wrap items-center gap-4">
              <button
                type="submit" disabled={sending || !form.sites}
                className="inline-flex items-center gap-2 font-sans text-sm font-bold bg-primary text-primary-foreground rounded-full pl-6 pr-5 py-3 hover:bg-white hover:text-ink transition-colors disabled:opacity-50"
                data-testid="preflight-submit"
              >
                {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                {sending ? "Sending…" : `Send campaign — ${files.length} ${files.length === 1 ? "ad" : "ads"}`}
              </button>
              <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                Goes straight to our ad ops team
              </span>
            </div>
            {submitError && (
              <p className="mt-4 font-sans text-sm text-red-400" data-testid="preflight-submit-error">{submitError}</p>
            )}
          </form>
        )}

        {/* Success */}
        {stage === "sent" && (
          <div className="mt-6 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-5 py-5" data-testid="preflight-success">
            <div className="flex items-start gap-3">
              <CheckCircle2 size={20} className="text-emerald-400 mt-0.5 shrink-0" />
              <div>
                <p className="font-sans text-sm font-bold text-white">
                  Campaign received — thank you, {form.name}.
                </p>
                <p className="font-sans text-sm text-white/65 mt-1">
                  {files.length} {files.length === 1 ? "creative" : "creatives"} for {form.company} have been sent to our ad ops team.
                  We'll be in touch at {form.email} to confirm scheduling.
                </p>
              </div>
            </div>
            <div className="mt-5 space-y-4" data-testid="preflight-display-preview">
              {files.map(({ result }, i) => (
                <div key={i}>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2">
                    {result.filename} — on-page display size {Math.round(result.width / 2)} × {Math.round(result.height / 2)}
                  </p>
                  <div className="bg-white rounded-xl px-6 py-5 flex justify-center">
                    <img
                      src={previews[i]}
                      width={Math.round(result.width / 2)}
                      height={Math.round(result.height / 2)}
                      className="max-w-full h-auto"
                      alt={`${result.filename} shown at its on-page display size`}
                      data-testid={`preflight-display-img-${i}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </FadeUp>
  );
};

export default AdPreflight;
